import "client-only";
// /lib/firebase/client.ts
// Import this ONLY from "use client" modules/components.
import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
    browserLocalPersistence,
    getAuth,
    setPersistence,
    type Auth,
} from "firebase/auth";
import {
    connectFirestoreEmulator,
    getFirestore,
    type Firestore,
} from "firebase/firestore";
import {
    connectStorageEmulator,
    getStorage,
    type FirebaseStorage,
} from "firebase/storage";

import { assertClientEnv, firebaseConfig, useEmulators } from "./config";

let _app: FirebaseApp | undefined;
let _auth: Auth | undefined;
let _db: Firestore | undefined;
let _storage: FirebaseStorage | undefined;

function ensureApp(): FirebaseApp {
    // Safe in HMR: reuse if already initialized
    if (!_app) {
        assertClientEnv();
        _app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    }
    return _app;
}

export function clientAuth(): Auth {
    if (!_auth) {
        const app = ensureApp();
        _auth = getAuth(app);
        // Set persistence only in the browser
        if (typeof window !== "undefined") {
            // change to browserSessionPersistence or inMemoryPersistence if you prefer
            setPersistence(_auth, browserLocalPersistence).catch(() => {});
        }
    }
    return _auth!;
}

export function clientDb(): Firestore {
    if (!_db) {
        const app = ensureApp();
        _db = getFirestore(app);
        if (useEmulators && typeof window !== "undefined") {
            // default emulator ports; change if needed
            try {
                connectFirestoreEmulator(_db, "127.0.0.1", 8080);
            } catch {}
        }
    }
    return _db!;
}

export function clientStorage(): FirebaseStorage {
    if (!_storage) {
        const app = ensureApp();
        _storage = getStorage(app);
        if (useEmulators && typeof window !== "undefined") {
            try {
                connectStorageEmulator(_storage, "127.0.0.1", 9199);
            } catch {}
        }
    }
    return _storage!;
}
